import { useCallback } from "react";

import { useObjectMemo } from "byzantine";

import { guideActions } from "../../../guides/storage";
import {
  handlePromptNavigation,
  useOnboardingEventHandlers,
} from "../../../guides/onboardingNavigation";
import { useExistingUserPrompts } from "../hooks/useExistingUserPrompts";
import { useCardStack } from "../hooks/useCardStack";
import { useCaughtUpAutoDismiss } from "../hooks/useCaughtUpAutoDismiss";

import type { OnboardingPrompt } from "byzantine/src/dbbl/businessLogic/modules/onboarding";

export const useExistingUserSidebarGuidesContainer = () => {
  const {
    isExistingGuide,
    isLoading,
    rawPrompts,
    visiblePrompts,
    guideState,
    setGuideState,
    dismissingUuid,
    dismissSpring,
    dismissPrompt,
  } = useExistingUserPrompts();

  const { stackedPrompts, caughtUpIsTop, newEntryUuids, getCardStyle } =
    useCardStack({
      visiblePrompts,
      rawPrompts,
      dismissingUuid,
      guideState,
    });

  const { caughtUpSpring } = useCaughtUpAutoDismiss({
    isTopCard: caughtUpIsTop,
    setGuideState,
  });

  // In-SPA completions (e.g. income reporting) flag a force-reset.
  const triggerForceReset = useCallback(() => {
    setGuideState(guideActions.setForceReset);
  }, [setGuideState]);

  const { eventHandlerMap, renderDialogs } = useOnboardingEventHandlers({
    onSuccess: triggerForceReset,
  });

  const handlePromptClick = useCallback(
    (prompt: OnboardingPrompt) => {
      handlePromptNavigation(prompt, { eventHandlerMap });
    },
    [eventHandlerMap]
  );

  return useObjectMemo({
    isExistingGuide,
    isLoading,
    rawPrompts,
    stackedPrompts,
    dismissingUuid,
    dismissSpring,
    dismissPrompt,
    caughtUpSpring,
    newEntryUuids,
    getCardStyle,
    handlePromptClick,
    renderDialogs,
  });
};
